import { FaxFormData, FormField, FormFieldConfig } from "../types";

/**
 * Extracts form values from Firebase document using firebaseKey mappings
 * @param firebaseData - The Firebase document data
 * @param formConfig - The form configuration with firebaseKey mappings
 * @returns FaxFormData with extracted values
 */
export const extractFirebaseValues = (
  firebaseData: any,
  formConfig: FormFieldConfig[],
): Partial<FaxFormData> => {
  const extractedData: Partial<FaxFormData> = {};

  // Check for specific Medicare non-oncology fields
  if (firebaseData?.submission_required_details) {
    console.log(
      "🚀 ~ medicare_non_oncology_is_cancer_therapy direct access:",
      firebaseData.submission_required_details
        .medicare_non_oncology_is_cancer_therapy,
    );
  }

  // Helper function to get nested value using dot notation
  const getNestedValue = (obj: any, path: string): any => {
    if (!obj || !path) return undefined;

    const keys = path.split(".");
    let current = obj;

    // Debug logging for isCancerTherapy path traversal
    if (path.includes("medicare_non_oncology_is_cancer_therapy")) {
      console.log(`🚀 ~ FIREBASE PATH TRAVERSAL for ${path}:`);
      console.log(`🚀 ~ Starting object:`, obj);
      console.log(`🚀 ~ Path keys:`, keys);
    }

    for (const key of keys) {
      if (current && typeof current === "object" && key in current) {
        if (path.includes("medicare_non_oncology_is_cancer_therapy")) {
          console.log(`🚀 ~ Found key "${key}":`, current[key]);
        }
        current = current[key];
      } else {
        if (path.includes("medicare_non_oncology_is_cancer_therapy")) {
          console.log(`🚀 ~ ❌ Missing key "${key}" in:`, current);
        }
        return undefined;
      }
    }

    if (path.includes("medicare_non_oncology_is_cancer_therapy")) {
      console.log(`🚀 ~ FINAL VALUE for ${path}:`, current);
    }

    return current;
  };

  // Helper function to normalize values based on form field type and options
  const normalizeValue = (value: any, field: FormField): any => {
    if (value === undefined || value === null || value === "") {
      return value;
    }

    // For radio and select fields, check if we need to normalize the value
    if (field.type === "radio" || field.type === "select") {
      const stringValue = String(value);

      // Debug for isCancerTherapy field
      if (field.name === "isCancerTherapy") {
        console.log(`🚀 ~ NORMALIZE - Processing radio field: ${field.name}`);
        console.log(`🚀 ~ NORMALIZE - String value: "${stringValue}"`);
        console.log(
          `🚀 ~ NORMALIZE - Available options:`,
          field.options?.map((opt) => opt.value),
        );
      }

      // Check if the field has options and find a matching option
      if (field.options) {
        // First try exact match
        const exactMatch = field.options.find(
          (option) => option.value === stringValue,
        );
        if (exactMatch) {
          if (field.name === "isCancerTherapy") {
            console.log(
              `🚀 ~ NORMALIZE - ✅ Exact match found: "${stringValue}"`,
            );
          }
          return stringValue;
        }

        // Try case-insensitive match for common yes/no values
        const lowerValue = stringValue.toLowerCase();
        const caseInsensitiveMatch = field.options.find(
          (option) => option.value.toLowerCase() === lowerValue,
        );
        if (caseInsensitiveMatch) {
          if (field.name === "isCancerTherapy") {
            console.log(
              `🚀 ~ NORMALIZE - ✅ Case-insensitive match found: "${stringValue}" -> "${caseInsensitiveMatch.value}"`,
            );
          }
          return caseInsensitiveMatch.value;
        }

        if (field.name === "isCancerTherapy") {
          console.log(
            `🚀 ~ NORMALIZE - ❌ No match found for: "${stringValue}"`,
          );
          console.log(
            `🚀 ~ NORMALIZE - Tried exact match with: "${stringValue}"`,
          );
          console.log(
            `🚀 ~ NORMALIZE - Tried case-insensitive match with: "${lowerValue}"`,
          );
        }
      }
    }

    // For boolean fields, handle string representations
    if (field.type === "checkbox") {
      if (typeof value === "string") {
        return value.toLowerCase() === "true" || value.toLowerCase() === "yes";
      }
      return Boolean(value);
    }

    // For custom field types, pass through as-is
    if (field.type === "previousDrugs" || field.type === "previousTreatments") {
      return value;
    }

    return value;
  };

  // Iterate through all sections and fields
  formConfig.forEach((section) => {
    section.fields.forEach((field) => {
      if (field.firebaseKey && firebaseData) {
        const rawValue = getNestedValue(firebaseData, field.firebaseKey);

        // Debug all fields to see what's being processed
        if (field.name === "isCancerTherapy") {
          console.log(`🚀 ~ Processing field: ${field.name}`);
          console.log(`🚀 ~ Firebase key: ${field.firebaseKey}`);
          console.log(
            `🚀 ~ Raw value: "${rawValue}" (type: ${typeof rawValue})`,
          );
          console.log(`🚀 ~ Field type: ${field.type}`);
          console.log(`🚀 ~ Field options:`, field.options);
        }

        // Only set the value if it exists and is not empty
        if (rawValue !== undefined && rawValue !== null && rawValue !== "") {
          const normalizedValue = normalizeValue(rawValue, field);
          extractedData[field.name as keyof FaxFormData] = normalizedValue;

          // Debug logging for specific fields
          if (
            field.name === "nccnGuidelines" ||
            field.name === "isCancerTherapy" ||
            field.name === "reviewedMBDL" ||
            field.name === "conditionRequested" ||
            field.name === "medicalReason"
          ) {
            console.log(
              `🚀 ~ ✅ EXTRACTED - Field: ${field.name}, Raw: "${rawValue}", Normalized: "${normalizedValue}", Type: ${field.type}`,
            );
            console.log(`🚀 ~ Field options:`, field.options);
            console.log(`🚀 ~ Firebase key: ${field.firebaseKey}`);
          }
        } else {
          // Debug when values are not extracted
          if (field.name === "isCancerTherapy") {
            console.log(
              `🚀 ~ ❌ NOT EXTRACTED - Field: ${field.name}, Raw value is empty/null/undefined: "${rawValue}"`,
            );
          }
        }
      } else {
        // Debug when fields are skipped
        if (field.name === "isCancerTherapy") {
          console.log(
            `🚀 ~ ⚠️ SKIPPED - Field: ${field.name}, Missing firebaseKey or firebaseData`,
          );
          console.log(`🚀 ~ Has firebaseKey: ${!!field.firebaseKey}`);
          console.log(`🚀 ~ Has firebaseData: ${!!firebaseData}`);
        }
      }
    });
  });

  // Return extracted data

  return extractedData;
};
